import { FC } from "react";
import { CircularProgress, css, styled, Chip } from "@mui/material";
import { IconExpandMore } from "@/assets/icons";
import { Select } from "@/components/elements/Select/Select";
import { background, labelColor, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { LEARNER_PLAN_STATUS, TLearnerPlanStatus } from "@/types/enum";
import { EMPTY_STR } from "@/constants/string";

const SELECT_WIDTH = 108;
const MENU_WIDTH = 134;

const iconStyle = { position: "absolute", right: 8, pointerEvents: "none", flexShrink: 0 } as const;

const PLAN_STATUS_BG: Record<TLearnerPlanStatus, string> = {
  [LEARNER_PLAN_STATUS.PREPARING]: labelColor.disable,
  [LEARNER_PLAN_STATUS.STUDYING]: labelColor.progress,
  [LEARNER_PLAN_STATUS.EXAM_TAKEN]: labelColor.success,
  [LEARNER_PLAN_STATUS.PASSED]: labelColor.lightGreen,
  [LEARNER_PLAN_STATUS.RETAKE_PREPARING]: labelColor.disable,
  [LEARNER_PLAN_STATUS.FAILED_ENDED]: labelColor.error,
};

const getStatusBg = (value: string): string => {
  return PLAN_STATUS_BG[value as TLearnerPlanStatus] ?? background.disable;
};

const SelectIconComponent: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  if (isLoading) {
    return <CircularProgress size={12} color="inherit" style={iconStyle} />;
  }
  return <IconExpandMore fontSize={16} color={text.secondary} style={iconStyle} />;
};

type TLearnerPlanStatusSelectProps = {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
};

export const LearnerPlanStatusSelect: FC<TLearnerPlanStatusSelectProps> = ({
  value,
  options,
  onChange,
  isLoading,
  disabled,
}) => {
  const bg = getStatusBg(value);
  const currentValueLabel = options.find((o) => o.value === value)?.label || value || EMPTY_STR.TEXT;

  if (disabled) {
    return <StyledDisabledChip label={currentValueLabel} bg={bg} />;
  }

  return (
    <StyledStatusSelect
      value={value}
      onChange={onChange}
      options={options}
      nonControl
      isShowIconComponent={false}
      statusBg={bg}
      disabled={isLoading}
      IconComponent={SelectIconComponent}
      MenuProps={{
        PaperProps: {
          sx: {
            width: MENU_WIDTH,
            mt: 0.5,
            "& .MuiMenuItem-root": {
              ...typography.cap12,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            },
          },
        },
      }}
    />
  );
};

const StyledStatusSelect = styled(Select, {
  shouldForwardProp: (p) => p !== "statusBg",
})<{ statusBg: string }>`
  width: ${SELECT_WIDTH}px;
  max-width: ${SELECT_WIDTH}px;
  min-width: ${SELECT_WIDTH}px;
  .MuiInputBase-root,
  &.MuiInputBase-root {
    border-radius: 999px !important;
    background: ${({ statusBg }) => statusBg};
  }
  fieldset {
    border: none !important;
  }
  .MuiInputBase-root.Mui-focused fieldset,
  &.Mui-focused fieldset {
    border: none !important;
  }
  .MuiInputBase-root.Mui-focused::after,
  &.Mui-focused::after {
    content: none !important;
    display: none !important;
  }
  .MuiInputBase-root .MuiSelect-select,
  &.MuiInputBase-root .MuiSelect-select,
  .MuiInputBase-root .MuiInputBase-input,
  &.MuiInputBase-root .MuiInputBase-input {
    ${css(typography.cap12)};
    padding: 4px 25px 4px 8px !important;
    color: ${text.primary} !important;
    background: transparent;
    border-radius: 999px !important;
    text-align: center;
  }
  .MuiSelect-select .MuiTypography-root,
  &.MuiInputBase-root .MuiSelect-select .MuiTypography-root {
    ${css(typography.cap12)};
  }
  .MuiSelect-icon {
    transform: none !important;
    right: 8px !important;
  }
`;

const StyledDisabledChip = styled(Chip, {
  shouldForwardProp: (p) => p !== "bg",
})<{ bg: string }>`
  width: ${SELECT_WIDTH}px;
  max-width: ${SELECT_WIDTH}px;
  height: auto;
  background-color: ${({ bg }) => bg};
  color: ${text.primary};
  border-radius: 999px;
  & .MuiChip-label {
    ${css(typography.cap12)};
    display: block;
    width: 100%;
    text-align: center;
    padding: 4px 14px;
  }
`;
